import React, { useState } from 'react';
import { BookOpen, Target, Layers, Eye, Headphones, Lightbulb, Sparkles, RotateCw, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../components/Header';

const ArticleCard = ({ icon: Icon, title, description, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.6)',
      borderRadius: '16px',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      overflow: 'hidden',
      marginBottom: '1rem'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          background: 'transparent',
          border: 'none',
          color: '#e2e8f0',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            padding: '0.75rem',
            background: 'rgba(96, 165, 250, 0.1)',
            borderRadius: '12px'
          }}>
            <Icon size={24} color="#60a5fa" />
          </div>
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#e2e8f0'
            }}>
              {title}
            </h3>
            {description && (
              <p style={{
                margin: '0.25rem 0 0 0',
                fontSize: '0.875rem',
                color: '#94a3b8'
              }}>
                {description}
              </p>
            )}
          </div>
        </div>
        {isOpen ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
      </button>
      
      {isOpen && (
        <div style={{
          padding: '0 1.5rem 1.5rem 1.5rem',
          borderTop: '1px solid rgba(100, 116, 139, 0.2)'
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

const ComboBox = ({ code, name, tiers, description }) => (
  <div style={{
    padding: '1rem',
    background: 'rgba(15, 23, 42, 0.6)',
    borderRadius: '8px',
    border: '1px solid rgba(100, 116, 139, 0.2)',
    marginBottom: '0.75rem'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      marginBottom: '0.5rem'
    }}>
      <code style={{
        padding: '0.25rem 0.5rem',
        background: 'rgba(96, 165, 250, 0.2)',
        borderRadius: '4px',
        color: '#60a5fa',
        fontSize: '0.875rem',
        fontWeight: '600',
        flexShrink: 0
      }}>
        {code}
      </code>
      <div style={{ flex: 1 }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: '600',
          color: '#e2e8f0',
          display: 'block',
          marginBottom: '0.5rem'
        }}>
          {name}
        </span>
        {tiers && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            {tiers.map((tier, index) => (
              <span
                key={index}
                style={{
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(167, 139, 250, 0.15)',
                  border: '1px solid rgba(167, 139, 250, 0.3)',
                  borderRadius: '4px',
                  color: '#c4b5fd',
                  fontWeight: '500'
                }}
              >
                {tier}
              </span>
            ))}
          </div>
        )}
        <p style={{
          margin: 0,
          fontSize: '0.875rem',
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

const TrainingGuidesPage = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: '#e2e8f0',
      fontFamily: '"DM Sans", system-ui, sans-serif'
    }}>
      <Header />
      
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Page Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            background: 'rgba(96, 165, 250, 0.1)',
            borderRadius: '16px',
            marginBottom: '1rem'
          }}>
            <BookOpen size={48} color="#60a5fa" />
          </div>
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '2.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Training Guides
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.1rem',
            color: '#94a3b8',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Expert-designed training programs to maximize your cognitive gains
          </p>
        </div>

        {/* Introduction */}
        <div style={{
          background: 'rgba(96, 165, 250, 0.1)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            margin: '0 0 0.75rem 0',
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#60a5fa'
          }}>
            Welcome to Your Training Hub
          </h2>
          <p style={{
            margin: 0,
            fontSize: '0.95rem',
            color: '#e2e8f0',
            lineHeight: '1.6'
          }}>
            With 24 different dimensions available in this app, the possibilities are endless! 
            This guide will help you choose the best combinations for your goals—whether you want 
            to push your working memory to the max, improve multitasking, or develop skills that 
            transfer to real life.
          </p>
        </div>

        {/* Training Programs */}
        <ArticleCard
          icon={Target}
          title="Deep Focus Training"
          description="Push your memory capacity to the limit"
          defaultOpen={false}
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            This program focuses on achieving the highest possible levels with simple combinations. 
            Think of it like lifting heavy weights—fewer dimensions mean you can go deeper and train 
            raw memory capacity.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="PA"
            name="Position + Letter Sound"
            tiers={['Beginner: 2-3 back', 'Intermediate: 4-5 back', 'Advanced: 6-7 back', 'Expert: 8+ back']}
            description="The classic combination. Track both where squares light up and which letters you hear. This is the gold standard for working memory training."
          />
          <ComboBox
            code="P"
            name="Position Only"
            tiers={['Beginner: 3-4 back', 'Intermediate: 5-6 back', 'Advanced: 7-8 back', 'Expert: 9+ back']}
            description="Pure spatial memory. With just one thing to track, you can really push how far back you can remember. Great for improving your sense of space and location."
          />
          <ComboBox
            code="A"
            name="Letter Sound Only"
            tiers={['Beginner: 3-4 back', 'Intermediate: 5-6 back', 'Advanced: 7-8 back', 'Expert: 9+ back']}
            description="Pure verbal memory. Focus entirely on the sequence of letters you hear. Excellent for language processing and auditory memory."
          />
          <ComboBox
            code="PC"
            name="Position + Color"
            tiers={['Beginner: 2-3 back', 'Intermediate: 4-5 back', 'Advanced: 6-7 back', 'Expert: 8+ back']}
            description="Visual-only training with two dimensions. Track where squares appear and what colors they are. Great for artists and visual thinkers."
          />
        </ArticleCard>

        <ArticleCard
          icon={Layers}
          title="Multitasking Mastery"
          description="Train your brain to juggle multiple things at once"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            This program trains your ability to pay attention to many things simultaneously. 
            You'll work with 4-6 dimensions at lower levels—like juggling multiple balls instead 
            of throwing one really high.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="PACSN"
            name="Position + Letter + Color + Shape + Number"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 3-4 back', 'Expert: 4+ back']}
            description="A challenging 5-way combination that trains divided attention. Track spatial position, what you hear, and three visual elements at once."
          />
          <ComboBox
            code="PCSFR"
            name="Position + Color + Shape + Face + Arrow"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 3-4 back', 'Expert: 4+ back']}
            description="Visual overload training with 5 visual dimensions. Great for improving visual processing speed and pattern recognition."
          />
          <ComboBox
            code="PAVMH"
            name="Position + Letter + Voice + Musical Note + Rhythm"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 3-4 back', 'Expert: 4+ back']}
            description="Multi-modal audio training. Track position, spoken letters, speaker identity, musical notes, and rhythmic patterns all at once."
          />
        </ArticleCard>

        <ArticleCard
          icon={Eye}
          title="Visual Intelligence"
          description="Enhance visual processing and spatial awareness"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            These combinations focus on visual and spatial dimensions. Perfect for designers, 
            architects, athletes, or anyone who wants to improve their visual memory and 
            spatial reasoning.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="PCSN"
            name="Position + Color + Shape + Number"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Classic visual training with four dimensions. Develops the ability to remember multiple visual features simultaneously."
          />
          <ComboBox
            code="PLZX"
            name="Position + Orbital + Size + Rotation"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Advanced spatial-geometric training. Track position, orbital location, circle sizes, and rotation angles—great for 3D thinking."
          />
          <ComboBox
            code="PRO"
            name="Position + Arrow + Movement"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Directional processing. Track positions, arrow directions, and movement patterns. Excellent for navigation and spatial orientation."
          />
          <ComboBox
            code="PBT"
            name="Position + Border + Pattern"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Edge detection and pattern recognition. Great for improving attention to visual details and border awareness."
          />
        </ArticleCard>

        <ArticleCard
          icon={Headphones}
          title="Auditory Excellence"
          description="Sharpen your listening and sound discrimination"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            These combinations develop your auditory memory and sound discrimination. 
            Perfect for musicians, language learners, or anyone who wants to improve 
            their listening skills.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="AM"
            name="Letter Sound + Musical Note"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Dual pitch training. Track both spoken letters and musical notes simultaneously. Great for developing perfect pitch awareness."
          />
          <ComboBox
            code="AVG"
            name="Letter + Voice + Volume"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Complex auditory processing. Track what's said, who's saying it, and how loud it is. Useful for improving conversation comprehension."
          />
          <ComboBox
            code="MHG"
            name="Musical Note + Rhythm + Volume"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Musical sophistication training. Develop pitch, timing, and dynamics awareness—perfect for musicians and music lovers."
          />
          <ComboBox
            code="DG"
            name="Mandarin Tone + Volume"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Tonal language training with volume variations. Excellent for learning tonal languages like Mandarin, Thai, or Vietnamese."
          />
        </ArticleCard>

        <ArticleCard
          icon={Lightbulb}
          title="Real-World Skills"
          description="Training that transfers to everyday life"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            These combinations are designed to improve cognitive skills you use in daily life—
            from navigation and conversations to mental math and music.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="PRO"
            name="Navigation Training"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Position + Arrow + Movement. Improves GPS navigation, spatial orientation, and your sense of direction in the real world."
          />
          <ComboBox
            code="AVU"
            name="Conversation Tracking"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Letter + Voice + Name Sound. Helps you remember who said what in conversations—great for meetings and social situations."
          />
          <ComboBox
            code="MH"
            name="Musical Memory"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Musical Note + Rhythm. Perfect for musicians and dancers. Improves your ability to remember melodies and rhythmic patterns."
          />
          <ComboBox
            code="PQ"
            name="Mental Math While Moving"
            tiers={['Beginner: 2 back', 'Intermediate: 3-4 back', 'Advanced: 5-6 back', 'Expert: 7+ back']}
            description="Position + Math. Trains your ability to do mental calculations while tracking spatial information—useful for quick calculations in daily life."
          />
          <ComboBox
            code="PAWQ"
            name="Cognitive Multitasking"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Position + Letter + Category Text + Math. Simulates real-world multitasking—remembering locations, verbal info, concepts, and numbers simultaneously."
          />
        </ArticleCard>

        <ArticleCard
          icon={Sparkles}
          title="Advanced Challenges"
          description="Unique and exotic combinations for experienced users"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            Ready to push your limits? These unusual combinations create novel cognitive challenges 
            that you won't find in traditional training programs.
          </p>
          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Recommended Combinations:
          </h4>
          <ComboBox
            code="DZXIG"
            name="Sensory Chaos"
            tiers={['Beginner: 1 back', 'Intermediate: 2 back', 'Advanced: 3 back', 'Expert: 4+ back']}
            description="Mandarin Tone + Circle Size + Rotation + Brightness + Volume. An intense 5-dimension challenge combining language, geometry, and audio."
          />
          <ComboBox
            code="LZXIB"
            name="Geometric Mastery"
            tiers={['Beginner: 1 back', 'Intermediate: 2 back', 'Advanced: 3 back', 'Expert: 4+ back']}
            description="Orbital Position + Circle Size + Rotation + Brightness + Border. Five geometric and spatial dimensions for advanced visual thinkers."
          />
          <ComboBox
            code="MHFR"
            name="Emotional-Musical Fusion"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Musical Note + Rhythm + Face + Arrow. Combine musical elements with emotional recognition and directional processing."
          />
          <ComboBox
            code="QWYC"
            name="Abstract Reasoning"
            tiers={['Beginner: 1-2 back', 'Intermediate: 2-3 back', 'Advanced: 4-5 back', 'Expert: 5+ back']}
            description="Math + Category Text + Name Text + Color. Four abstract dimensions that challenge your conceptual and symbolic thinking."
          />
        </ArticleCard>

        <ArticleCard
          icon={RotateCw}
          title="The Rotation System"
          description="Keep your training fresh and prevent plateaus"
        >
          <p style={{ color: '#e2e8f0', lineHeight: '1.6', marginTop: '1rem' }}>
            To maximize long-term progress, consider using a rotation system. Instead of training 
            the same combinations forever, cycle through a larger pool to keep your brain challenged 
            and engaged.
          </p>
          
          <div style={{
            background: 'rgba(167, 139, 250, 0.1)',
            border: '1px solid rgba(167, 139, 250, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{
              margin: '0 0 1rem 0',
              color: '#a78bfa',
              fontSize: '1.1rem'
            }}>
              How It Works:
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: '1.5rem',
              color: '#e2e8f0',
              lineHeight: '1.8'
            }}>
              <li>Choose 8 combinations from the programs above</li>
              <li>Train only 4 of them each week (your "active window")</li>
              <li>Every week, drop the oldest combo and add a new one</li>
              <li>Each combo stays active for 4 weeks, then rests for 4 weeks</li>
              <li>This creates natural spacing for better long-term retention</li>
            </ul>
          </div>

          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Example 8-Combo Pool (Balanced Mix):
          </h4>
          
          <div style={{
            background: 'rgba(15, 23, 42, 0.6)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(100, 116, 139, 0.2)'
          }}>
            <ol style={{
              margin: 0,
              paddingLeft: '1.5rem',
              color: '#e2e8f0',
              lineHeight: '2'
            }}>
              <li><strong style={{ color: '#60a5fa' }}>Position + Letter Sound</strong> — Classic dual n-back (depth training)</li>
              <li><strong style={{ color: '#60a5fa' }}>Position + Letter + Color + Shape + Number</strong> — 5-dimension breadth challenge</li>
              <li><strong style={{ color: '#60a5fa' }}>Position + Arrow + Movement</strong> — Navigation and directional processing</li>
              <li><strong style={{ color: '#60a5fa' }}>Letter + Voice + Volume</strong> — Audio tracking with speaker identity</li>
              <li><strong style={{ color: '#60a5fa' }}>Position + Color + Shape + Number</strong> — Visual mastery with 4 dimensions</li>
              <li><strong style={{ color: '#60a5fa' }}>Musical Note + Rhythm</strong> — Musical intelligence and timing</li>
              <li><strong style={{ color: '#60a5fa' }}>Position + Letter + Category Text + Math</strong> — Real-world multitasking</li>
              <li><strong style={{ color: '#60a5fa' }}>Orbital Position + Circle Size + Rotation + Brightness + Border</strong> — Geometric challenge</li>
            </ol>
          </div>

          <h4 style={{ color: '#60a5fa', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Example Weekly Schedule:
          </h4>
          
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            <div style={{
              padding: '1rem',
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '8px',
              borderLeft: '3px solid #60a5fa'
            }}>
              <div style={{ fontWeight: '600', color: '#60a5fa', marginBottom: '0.5rem' }}>
                Week 1: Foundation
              </div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8', lineHeight: '1.6' }}>
                Train combos #1, #2, #3, #4 from your pool
              </div>
            </div>
            
            <div style={{
              padding: '1rem',
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '8px',
              borderLeft: '3px solid #a78bfa'
            }}>
              <div style={{ fontWeight: '600', color: '#a78bfa', marginBottom: '0.5rem' }}>
                Week 2: First Rotation
              </div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8', lineHeight: '1.6' }}>
                Drop combo #1, add combo #5 → Train combos #2, #3, #4, #5
              </div>
            </div>
            
            <div style={{
              padding: '1rem',
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '8px',
              borderLeft: '3px solid #10b981'
            }}>
              <div style={{ fontWeight: '600', color: '#10b981', marginBottom: '0.5rem' }}>
                Week 3: Continue Rotating
              </div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8', lineHeight: '1.6' }}>
                Drop combo #2, add combo #6 → Train combos #3, #4, #5, #6
              </div>
            </div>
            
            <div style={{
              padding: '1rem',
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '8px',
              borderLeft: '3px solid #f59e0b'
            }}>
              <div style={{ fontWeight: '600', color: '#f59e0b', marginBottom: '0.5rem' }}>
                Week 4-8 and Beyond
              </div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                Keep cycling through your 8-combo pool. After week 8, combo #1 returns to the rotation!
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '8px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: '#10b981',
              fontWeight: '600',
              marginBottom: '0.5rem'
            }}>
              <ArrowRight size={18} />
              <span>Training Frequency</span>
            </div>
            <p style={{
              margin: 0,
              fontSize: '0.875rem',
              color: '#e2e8f0',
              lineHeight: '1.6'
            }}>
              For each active combo, train 2-3 times per week. That's 8-12 total sessions per week, 
              giving you consistent practice while avoiding burnout.
            </p>
          </div>
        </ArticleCard>

        {/* Footer Tips */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          padding: '1.5rem',
          marginTop: '2rem'
        }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#60a5fa'
          }}>
            Pro Tips for Success
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '1.5rem',
            color: '#e2e8f0',
            lineHeight: '1.8'
          }}>
            <li><strong>Start where you are:</strong> It's okay to begin at 1-back or 2-back. Everyone starts somewhere!</li>
            <li><strong>Aim for 80-90% accuracy:</strong> If you're getting everything right, increase the level. If you're below 70%, drop down.</li>
            <li><strong>Stay consistent:</strong> 3-4 sessions per week is better than cramming 10 sessions in one day.</li>
            <li><strong>Mix it up:</strong> Don't just stick to one combination. Variety keeps your brain adapting and growing.</li>
            <li><strong>Be patient:</strong> Real cognitive gains take weeks and months, not days. Trust the process!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TrainingGuidesPage;

